<script lang="ts">
import { isMobile } from '../../utils/breakpoints';
import safeFetch from '../../utils/safeFetch';
import { computed, onMounted, ref, watch } from 'vue';
import { getVendorConsent, registerConsentListeners } from '../../globals/consentmanager';
import checkAgentsAvailability, {
  isAgentAvailable,
  updateConsentStatus,
} from '../../globals/checkAgentAvailability';
import cmpVendorIDs from '../../globals/constants/cmpVendorIDs';
import { getMarketingConsent } from '../../globals/cookiebot';
import { regionDataUrl } from '../../shared/globals';
import Modal from '../Modal/Modal.vue';
import Select from '../Select/Select.vue';
import Icon from '../Icon/Icon.vue';
import Textfield1XP from '../Textfield1XP/Textfield1XP.vue';
import Button1XP from '../Button1XP/Button1XP.vue';
import type { WindowWika } from '../../types/window';

const vendor = cmpVendorIDs.MICROSOFT_LIVE_CHAT;
const _window = window as unknown as WindowWika;

type Props = {
  config?: {
    infoPopperContent?: string;
    infoPopperImage?: string;
    input?: {
      submitUrl: string;
      placeholderText: string;
    };
    jobs?: {
      submitUrl: string;
      jsonUrl: string;
      dropdownPlaceholder: string;
      dropdownPlaceholderB: string;
    };
    resetSelectOption: string;
    worldwide?: {
      submitUrl: string;
      dropdownPlaceholder: string;
    };
    static?: {
      email: string;
      phone: string;
    };
    title?: string;
    icon: string;
    linkUrl?: string;
    text?: string;
    shop?: {
      phone: string;
      opening: string;
    };
    dropdown?: {
      dropdownPlaceholder: string;
      dropdownValues: string[];
    };
    buttons: Array<{
      linkUrl: string;
      icon: string;
      linkTarget: string;
      text: string;
      offlineText: string;
      offlineRef: string;
    }>;
  };
};
</script>

<script setup lang="ts">
const consentGiven = ref(_window.Cookiebot ? getMarketingConsent() : getVendorConsent(vendor.id));
const isModalOpen = ref(false);

const { config } = defineProps<Props>();

const apiData = ref<{
  items: Array<{
    filterCategories: {
      country: string;
      targetgroup: string[];
    };
  }>;
  regions: Array<{
    name: string;
    countries: Array<{
      name: string;
    }>;
  }>;
} | null>(null);
const companySelectOptions = ref<string[]>([]);
const countrySelectOptions = ref<string[]>([]);
const dropdownOneValue = ref('');
const inputField = ref('');
const regionSelectOptions = ref<string[]>([]);
const showDropdownTwo = ref(false);

const handleSubmit = (e) => {
  e.preventDefault();
  _window.wtTrack?.({ cat: 'SALESBOXCLICK' }, { inputIdent: inputField.value });
  _window.location.href = `${config?.input?.submitUrl}${inputField.value}`;
};

const handleSelectSubmit = (value) => {
  if (value.submitUrl) {
    _window.wtTrack?.({ cat: 'SALESBOXCLICK' }, { inputIdent: value.name });
    _window.location = value.submitUrl;
  }
};

const handleRegionSelect = (value) => {
  if (value !== '') {
    companySelectOptions.value = [];
    countrySelectOptions.value = [];
    dropdownOneValue.value = value;
    showDropdownTwo.value = true;

    if (config?.jobs) {
      const targetgroups: string[] = [];
      for (let region of apiData?.value?.items ?? []) {
        if (
          region.filterCategories.country === dropdownOneValue.value ||
          value === config.resetSelectOption
        ) {
          for (let targetgroup of region.filterCategories.targetgroup) {
            targetgroups.push(targetgroup);
          }
        }
      }
      companySelectOptions.value.push(config.resetSelectOption);
      companySelectOptions.value.push(...filterUniqueObjects(targetgroups));
    } else if (apiData?.value?.regions) {
      for (let region of apiData.value.regions) {
        if (region.name === dropdownOneValue.value) {
          for (let country of region.countries) {
            countrySelectOptions.value.push(country.name);
          }
        }
      }
    }
  }
};

const handleCountrySelect = (value) => {
  _window.wtTrack?.(
    { cat: 'SALESBOXCLICK' },
    { inputIdent: `${dropdownOneValue.value} - ${value}` },
  );

  if (config?.worldwide) {
    _window.location.href = `${config.worldwide.submitUrl}&cnEntry=${dropdownOneValue.value}&country=${value}`;
  }
  if (config?.jobs) {
    if (config.resetSelectOption === value) value = '';
    if (config.resetSelectOption === dropdownOneValue.value) dropdownOneValue.value = '';

    _window.location.href = `${config.jobs.submitUrl}?targetgroup=${value}&country=${dropdownOneValue.value}`;
  }
};
const startChat = () => {
  if (!consentGiven.value) {
    isModalOpen.value = true;
    return;
  }
  triggerChat();
};
const startChatApprove = () => {
  setTimeout(() => {
    triggerChat();
  }, 4000);
};
const triggerChat = () => {
  if (_window.LC_API && _window.LC_API.is_loaded?.() && 'open_chat_window' in _window.LC_API) {
    _window.LC_API.open_chat_window();
    _window.wtTrack?.({ cat: 'LIVECHATBUTTONCLICK' });

    return;
  }
  if (_window?.Microsoft?.Omnichannel?.LiveChatWidget?.SDK?.startChat) {
    if (isAgentAvailable.value === true) {
      _window?.Microsoft?.Omnichannel?.LiveChatWidget?.SDK?.startChat();
    }
    _window.wtTrack?.({ cat: 'LIVECHATBUTTONCLICK' });

    return;
  }
};
const filterUniqueObjects = (data) => {
  return data.filter((value, index, self) => {
    return self.indexOf(value) === index;
  });
};

const initApiData = (data) => {
  if (config?.jobs) {
    apiData.value = data;
    regionSelectOptions.value.push(
      config.resetSelectOption,
      ...filterUniqueObjects(
        apiData?.value?.items.map((region) => region.filterCategories.country),
      ),
    );
  } else if (data.regions) {
    apiData.value = data;
    regionSelectOptions.value.push(
      ...(apiData?.value?.regions?.map((region) => region.name) ?? []),
    );
  }
};

const mailtoLink = computed(() => {
  if (config?.static) {
    return `mailto:${config.static.email}`;
  }
  return '';
});

function cleanTitleAttributes() {
  const docLinks = document.querySelectorAll<HTMLAnchorElement>('.HeaderBox__docLink');
  if (docLinks) {
    docLinks.forEach((link) => {
      if (link.title) {
        link.title = link.title.replace(/<[^>]*>?/gm, '');
      }
    });
  }
}

const checkVendorConsent = () => {
  consentGiven.value = getVendorConsent(vendor.id);

  updateConsentStatus(consentGiven.value);
  checkAgentsAvailability();
};

const registerConsentListenersWithDelay = (checkVendorConsent) => {
  const intervalId = setInterval(() => {
    if (typeof _window.__cmp === 'function') {
      registerConsentListeners(checkVendorConsent);
      clearInterval(intervalId);
    }
  }, 100);
  setTimeout(() => clearInterval(intervalId), 5000);
};

onMounted(() => {
  checkAgentsAvailability();

  if (_window.cmpConsentType === 'consentmanager') {
    registerConsentListenersWithDelay(checkVendorConsent);
    setTimeout(() => {
      checkVendorConsent();
    }, 1000);
  }
  cleanTitleAttributes();
  let dataUrl = '';
  if (config?.worldwide) {
    dataUrl = _window.regionDataUrl || regionDataUrl;
  } else if (config?.jobs) {
    dataUrl = config.jobs.jsonUrl;
  }

  setInterval(() => {
    checkAgentsAvailability();
  }, 30000);

  if (dataUrl) {
    safeFetch(dataUrl)
      .then((response) => {
        if (!response) {
          // TODO: add handling for undefined
          return;
        }
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => initApiData(data))
      .catch((error) => console.error(error));
  }
});
watch(consentGiven, (newConsent) => {
  toggleChatWidget(newConsent);
});
function toggleChatWidget(consentGiven) {
  const chatIframe = document.getElementById('Microsoft_Omnichannel_LCWidget_Chat_Iframe_Window');

  if (chatIframe) {
    if (consentGiven) {
      chatIframe.style.display = 'block';
    } else {
      chatIframe.style.display = 'none';
    }
  }
}
watch(
  () => consentGiven.value,
  (newValue) => {
    if (!newValue) {
      isModalOpen.value = false;
    }
  },
);
</script>

<template>
  <Modal
    v-if="!consentGiven"
    :is-chat="true"
    :is-modal-open="isModalOpen"
    @close-modal="isModalOpen = false"
    @consent-updated="consentGiven = $event"
    @start-chat="startChatApprove"
  />
  <div v-if="config" :class="$style.container">
    <div :class="$style.upper">
      <div :class="$style.upperText">
        <div v-if="config.title" :class="$style.textContainer">
          <Icon :name="config.icon" size="xLarge" />
          <span v-if="config.shop" :class="$style.title" v-html="config.title" />
          <template v-else>
            <a v-if="config.linkUrl" :class="$style.link" :href="config.linkUrl">
              <span :class="$style.title" v-html="config.title" />
            </a>
            <span v-else :class="$style.title" v-html="config.title" />
          </template>
          <tippy
            v-if="config.infoPopperContent"
            v-tippy="{
              arrow: true,
              distance: 7,
              interactive: true,
              theme: '1xp light tooltip',
              trigger: isMobile ? 'click' : 'mouseenter focus',
              zIndex: 8000000,
            }"
          >
            <Icon :class="$style.info" name="info" size="large" />
            <template #content>
              <div :class="$style.infoContent">
                <div v-html="config.infoPopperContent" />
                <div :class="$style.infoImage">
                  <img v-if="config.infoPopperImage" alt="" :src="config.infoPopperImage" />
                </div>
              </div>
            </template>
          </tippy>
        </div>

        <div v-if="config.text || config.static || config.shop" :class="$style.text">
          <a v-if="config.linkUrl" :class="$style.link" :href="config.linkUrl">
            {{ config.text }}
          </a>
          <template v-else>
            {{ config.text }}
          </template>

          <template v-if="config.static">
            <div>{{ config.static.phone }}</div>
            <a :class="[$style.link]" :href="mailtoLink">{{ config.static.email }}</a>
          </template>

          <template v-if="config.shop">
            <div>
              {{ config.shop.phone }}
            </div>
            <div v-html="config.shop.opening" />
          </template>
        </div>
      </div>

      <form
        v-if="config.input || config.dropdown || config.worldwide || config.jobs"
        method="GET"
        @submit.prevent="handleSubmit"
      >
        <div :class="$style.userInput">
          <template v-if="config.input">
            <div :class="$style.postalCode">
              <Textfield1XP
                aria-label="postal code"
                close-label=""
                label="Search"
                :placeholder="config.input.placeholderText"
                :value="inputField"
                @input="
                  (event) => {
                    inputField = event.currentTarget.value ?? '';
                  }
                "
              />
              <Button1XP
                aria-label="submit postal code"
                button-type="submit"
                :class="$style.submit"
                :disabled="inputField.length === 0"
                size="s"
                start-icon="arrow_forward"
                variant="secondary"
                @click="handleSubmit"
              />
            </div>
          </template>

          <Select
            v-else-if="config.dropdown"
            :class="$style.select"
            colour="oneXP"
            label="name"
            :placeholder="config.dropdown.dropdownPlaceholder"
            :select-options="config.dropdown.dropdownValues"
            select-value=""
            track-by="name"
            @on-change="handleSelectSubmit"
          />

          <template v-else-if="config.jobs">
            <Select
              :class="$style.select"
              colour="oneXP"
              :placeholder="config.jobs.dropdownPlaceholder"
              :select-options="regionSelectOptions"
              select-value=""
              @on-change="handleRegionSelect"
            />
            <Select
              :key="`jobCompanySelectKey-${dropdownOneValue}`"
              :class="$style.select"
              colour="oneXP"
              :disabled="!showDropdownTwo"
              :placeholder="config.jobs.dropdownPlaceholderB"
              :select-options="companySelectOptions"
              select-value=""
              @on-change="handleCountrySelect"
            />
          </template>

          <template v-else-if="config.worldwide">
            <Select
              :class="$style.select"
              colour="oneXP"
              :placeholder="config.worldwide.dropdownPlaceholder"
              :select-options="regionSelectOptions"
              select-value=""
              @on-change="handleRegionSelect"
            />
            <Select
              :key="`worldwideCountrySelectKey-${dropdownOneValue}`"
              :class="$style.select"
              colour="oneXP"
              :disabled="!showDropdownTwo"
              :placeholder="config.worldwide.dropdownPlaceholder"
              :select-options="countrySelectOptions"
              select-value=""
              @on-change="handleCountrySelect"
            />
          </template>
        </div>
      </form>
    </div>

    <div v-if="config.buttons" :class="$style.lower">
      <template v-for="(button, index) in config.buttons" :key="index">
        <Button1XP
          v-if="button.linkUrl"
          :class="$style.button"
          :start-icon="button.icon"
          :target="button.linkTarget"
          :text="button.text"
          :url="button.linkUrl"
          variant="secondary"
        />
        <Button1XP
          v-else
          :class="$style.button"
          :start-icon="button.icon"
          :target="!isAgentAvailable ? '_self' : undefined"
          :text="isAgentAvailable ? button.text : button.offlineText"
          :url="!isAgentAvailable && button.offlineRef ? button.offlineRef : undefined"
          variant="secondary"
          @click="isAgentAvailable ? startChat() : undefined"
        />
      </template>
    </div>
  </div>
</template>

<style module lang="scss">
@forward './SalesContainer.scss';
</style>
